import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, AlertTriangle, PackagePlus, History } from "lucide-react";
import UploadButton from "./UploadButton";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "../ui/table";
import { toast } from "sonner";
import api, { eur, formatApiErrorDetail } from "../../lib/api";

const CAT_LABELS = { fusti: "Fusto 19L", liquori: "Liquore", granite: "Granita", collezione: "Collezione" };
const emptyProduct = { name: "", category: "fusti", format: "", description: "", public_price: 0, agent_price: 0, image_url: "", active: true, stock: 50, low_stock_threshold: 10, ingredients: "", portions: 130, vintage: "", distillery: "", abv: "", limited_edition: "", bottle_number: "", certificate: false };

export default function ProductsPanel() {
  const [products, setProducts] = useState([]);
  const [prodForm, setProdForm] = useState(emptyProduct);
  const [prodEditId, setProdEditId] = useState(null);
  const [prodOpen, setProdOpen] = useState(false);
  const [stockForm, setStockForm] = useState({ open: false, product: null, type: "carico", quantity: 0, note: "" });
  const [moves, setMoves] = useState({ open: false, product: null, list: [] });

  const load = () => api.get("/products").then(({ data }) => setProducts(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openProduct = (p) => { setProdForm(p ? { ...p } : emptyProduct); setProdEditId(p?.id || null); setProdOpen(true); };
  const saveProduct = async () => {
    try {
      const payload = { ...prodForm, public_price: Number(prodForm.public_price), agent_price: Number(prodForm.agent_price), stock: Number(prodForm.stock), low_stock_threshold: Number(prodForm.low_stock_threshold), portions: Number(prodForm.portions) || 0 };
      if (prodEditId) await api.put(`/products/${prodEditId}`, payload);
      else await api.post("/products", payload);
      toast.success("Prodotto salvato"); setProdOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const delProduct = async (id) => { await api.delete(`/products/${id}`); toast.success("Eliminato"); load(); };
  const saveStock = async () => {
    try {
      await api.post(`/products/${stockForm.product.id}/stock`, { type: stockForm.type, quantity: Number(stockForm.quantity), note: stockForm.note });
      toast.success("Movimento scorte registrato"); setStockForm({ open: false, product: null, type: "carico", quantity: 0, note: "" }); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const openMoves = async (p) => {
    try { const { data } = await api.get(`/products/${p.id}/movements`); setMoves({ open: true, product: p, list: data }); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const lowStock = products.filter((p) => p.stock != null && p.stock <= (p.low_stock_threshold ?? 10));

  return (
    <div>
      {lowStock.length > 0 && (
        <div className="mb-4 flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm" data-testid="low-stock-alert">
          <AlertTriangle className="w-4 h-4" /> {lowStock.length} prodotti con scorta bassa
        </div>
      )}
      <div className="flex justify-end mb-4">
        <Dialog open={prodOpen} onOpenChange={setProdOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => openProduct(null)} data-testid="add-product-btn" className="bg-[#0F4C81] text-white rounded-full"><Plus className="w-4 h-4 mr-2" />Nuovo prodotto</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-serif text-2xl">{prodEditId ? "Modifica" : "Nuovo"} prodotto</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Nome</Label><Input data-testid="product-name-input" value={prodForm.name} onChange={(e) => setProdForm({ ...prodForm, name: e.target.value })} className="mt-1" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Categoria</Label>
                  <Select value={prodForm.category} onValueChange={(v) => setProdForm({ ...prodForm, category: v })}>
                    <SelectTrigger className="mt-1" data-testid="product-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fusti">Fusto 19L</SelectItem>
                      <SelectItem value="liquori">Liquore</SelectItem>
                      <SelectItem value="granite">Granita/Preparato</SelectItem>
                      <SelectItem value="collezione">Distillato da Collezione</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Formato</Label><Input value={prodForm.format} onChange={(e) => setProdForm({ ...prodForm, format: e.target.value })} className="mt-1" placeholder="Fusto 19L…" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Prezzo pubblico €</Label><Input type="number" step="0.01" data-testid="product-public-price-input" value={prodForm.public_price} onChange={(e) => setProdForm({ ...prodForm, public_price: e.target.value })} className="mt-1" /></div>
                <div><Label>Prezzo agente €</Label><Input type="number" step="0.01" data-testid="product-agent-price-input" value={prodForm.agent_price} onChange={(e) => setProdForm({ ...prodForm, agent_price: e.target.value })} className="mt-1" /></div>
              </div>
              <div><Label>Descrizione</Label><Textarea value={prodForm.description} onChange={(e) => setProdForm({ ...prodForm, description: e.target.value })} className="mt-1" /></div>
              <div>
                <Label>Immagine prodotto</Label>
                <div className="flex gap-2 mt-1 items-center">
                  {prodForm.image_url ? <img src={prodForm.image_url} alt="" className="w-12 h-12 rounded-lg object-cover border" /> : null}
                  <Input value={prodForm.image_url} onChange={(e) => setProdForm({ ...prodForm, image_url: e.target.value })} placeholder="URL o carica immagine" className="flex-1" data-testid="product-image-input" />
                  <UploadButton onUploaded={(url) => setProdForm({ ...prodForm, image_url: url })} testid="product-image-upload" />
                </div>
              </div>
              <div><Label>Ingredienti</Label><Textarea data-testid="product-ingredients-input" value={prodForm.ingredients} onChange={(e) => setProdForm({ ...prodForm, ingredients: e.target.value })} className="mt-1" placeholder="Es. Alcool, zucchero, acqua, scorze di limone" /></div>
              {prodForm.category === "fusti" && (
                <div><Label>Porzioni per fusto (drink)</Label><Input type="number" data-testid="product-portions-input" value={prodForm.portions ?? 130} onChange={(e) => setProdForm({ ...prodForm, portions: e.target.value })} className="mt-1" placeholder="130" /></div>
              )}
              {prodForm.category === "collezione" && (
                <div className="space-y-3 border border-amber-200 rounded-xl p-3 bg-amber-50/50" data-testid="collection-fields">
                  <div className="text-xs font-semibold uppercase tracking-wider text-amber-700">Dati da collezione</div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Annata</Label><Input data-testid="product-vintage-input" value={prodForm.vintage || ""} onChange={(e) => setProdForm({ ...prodForm, vintage: e.target.value })} className="mt-1" placeholder="Es. 1998" /></div>
                    <div><Label>Gradazione</Label><Input data-testid="product-abv-input" value={prodForm.abv || ""} onChange={(e) => setProdForm({ ...prodForm, abv: e.target.value })} className="mt-1" placeholder="Es. 43% vol" /></div>
                  </div>
                  <div><Label>Distilleria</Label><Input data-testid="product-distillery-input" value={prodForm.distillery || ""} onChange={(e) => setProdForm({ ...prodForm, distillery: e.target.value })} className="mt-1" placeholder="Es. Maison Cilento" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Edizione limitata</Label><Input data-testid="product-limited-input" value={prodForm.limited_edition || ""} onChange={(e) => setProdForm({ ...prodForm, limited_edition: e.target.value })} className="mt-1" placeholder="Es. 1 di 300" /></div>
                    <div><Label>N° bottiglia</Label><Input data-testid="product-bottlenum-input" value={prodForm.bottle_number || ""} onChange={(e) => setProdForm({ ...prodForm, bottle_number: e.target.value })} className="mt-1" placeholder="Es. N. 042/300" /></div>
                  </div>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" data-testid="product-certificate-input" checked={!!prodForm.certificate} onChange={(e) => setProdForm({ ...prodForm, certificate: e.target.checked })} className="w-4 h-4 accent-amber-600" />
                    Con certificato di autenticità
                  </label>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Giacenza (scorte)</Label><Input type="number" data-testid="product-stock-input" value={prodForm.stock} onChange={(e) => setProdForm({ ...prodForm, stock: e.target.value })} className="mt-1" /></div>
                <div><Label>Soglia scorta bassa</Label><Input type="number" value={prodForm.low_stock_threshold} onChange={(e) => setProdForm({ ...prodForm, low_stock_threshold: e.target.value })} className="mt-1" /></div>
              </div>
            </div>
            <DialogFooter><Button onClick={saveProduct} data-testid="save-product-btn" className="bg-[#0F4C81] text-white rounded-full">Salva</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="bg-white rounded-2xl border border-stone-200 overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Prodotto</TableHead><TableHead>Categoria</TableHead><TableHead>Pubblico</TableHead><TableHead>Agente</TableHead><TableHead>Scorte</TableHead><TableHead>Azioni</TableHead></TableRow></TableHeader>
          <TableBody>
            {products.map((p) => (
              <TableRow key={p.id} data-testid={`admin-product-${p.id}`}>
                <TableCell className="font-medium">{p.name}<div className="text-xs text-stone-400">{p.format}</div></TableCell>
                <TableCell><span className="text-xs px-2 py-1 rounded-full bg-stone-100">{CAT_LABELS[p.category]}</span></TableCell>
                <TableCell className="font-mono">{eur(p.public_price)}</TableCell>
                <TableCell className="font-mono text-[#0F4C81]">{eur(p.agent_price)}</TableCell>
                <TableCell>
                  <span className="font-mono">{p.stock ?? "—"}</span>
                  {p.stock != null && p.stock <= (p.low_stock_threshold ?? 10) && <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700">bassa</span>}
                </TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <button onClick={() => openMoves(p)} data-testid={`stock-history-${p.id}`} title="Storico scorte"><History className="w-4 h-4 text-stone-500 hover:text-[#0F4C81]" /></button>
                    <button onClick={() => setStockForm({ open: true, product: p, type: "carico", quantity: 0, note: "" })} data-testid={`stock-load-${p.id}`} title="Movimento scorte"><PackagePlus className="w-4 h-4 text-stone-500 hover:text-[#15803D]" /></button>
                    <button onClick={() => openProduct(p)} data-testid={`edit-product-${p.id}`}><Pencil className="w-4 h-4 text-stone-500 hover:text-[#0F4C81]" /></button>
                    <button onClick={() => delProduct(p.id)} data-testid={`delete-product-${p.id}`}><Trash2 className="w-4 h-4 text-red-500" /></button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <Dialog open={stockForm.open} onOpenChange={(v) => setStockForm({ ...stockForm, open: v })}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Movimento scorte</DialogTitle></DialogHeader>
          <p className="text-sm text-stone-500">{stockForm.product?.name} — giacenza attuale: <b>{stockForm.product?.stock ?? 0}</b></p>
          <div className="space-y-3">
            <div>
              <Label>Tipo movimento</Label>
              <Select value={stockForm.type} onValueChange={(v) => setStockForm({ ...stockForm, type: v })}>
                <SelectTrigger className="mt-1" data-testid="stock-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="carico">Carico (produzione +)</SelectItem>
                  <SelectItem value="scarico">Scarico (−)</SelectItem>
                  <SelectItem value="rettifica">Rettifica (imposta giacenza)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>{stockForm.type === "rettifica" ? "Nuova giacenza" : "Quantità"}</Label><Input type="number" min="0" data-testid="stock-load-qty-input" value={stockForm.quantity} onChange={(e) => setStockForm({ ...stockForm, quantity: e.target.value })} className="mt-1" /></div>
            <div><Label>Nota</Label><Input value={stockForm.note} onChange={(e) => setStockForm({ ...stockForm, note: e.target.value })} className="mt-1" placeholder="Es. lotto del 09/06" /></div>
          </div>
          <DialogFooter><Button onClick={saveStock} data-testid="save-stock-btn" className="bg-[#15803D] text-white rounded-full">Registra movimento</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={moves.open} onOpenChange={(v) => setMoves({ ...moves, open: v })}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Storico scorte — {moves.product?.name}</DialogTitle></DialogHeader>
          <Table>
            <TableHeader><TableRow><TableHead>Data</TableHead><TableHead>Tipo</TableHead><TableHead>Q.tà</TableHead><TableHead>Giacenza</TableHead><TableHead>Nota</TableHead></TableRow></TableHeader>
            <TableBody>
              {moves.list.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-stone-400 py-6">Nessun movimento</TableCell></TableRow>}
              {moves.list.map((m) => (
                <TableRow key={m.id}>
                  <TableCell className="text-xs">{new Date(m.at).toLocaleDateString("it-IT")}</TableCell>
                  <TableCell><span className="text-xs px-2 py-0.5 rounded-full bg-stone-100">{m.type}</span></TableCell>
                  <TableCell className="font-mono">{m.quantity}</TableCell>
                  <TableCell className="font-mono">{m.stock_after}</TableCell>
                  <TableCell className="text-xs text-stone-500">{m.note}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>
    </div>
  );
}
